// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — the menu / help / about / results markup. Attribution backlink is mandatory
// (it's how players find the rest of the catalogue) and licence copy never appears here.

import { escapeHtml, fmtScore } from './dom';
import { MODE_IDS, modeOf, type Mode, type ModeId } from './modes';
import type { Summary } from './game';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

function modeChips(active: ModeId): string {
  return MODE_IDS.map(
    (id) =>
      `<button class="chip${id === active ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === active}">${escapeHtml(
        modeOf(id).name,
      )}</button>`,
  ).join('');
}

export function menuHtml(o: { modeId: ModeId; best: number; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  return `
  <section class="screen menu">
    <header class="brand">
      <div class="brand-mote" aria-hidden="true"><span></span></div>
      <h1>Warble</h1>
      <p class="tag">Hum, whistle or sing to fly along the ribbon of light.</p>
    </header>

    <div class="modes" role="group" aria-label="Choose a mode">${modeChips(o.modeId)}</div>
    <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>

    <div class="menu-actions">
      <button class="btn primary" id="playSolo">Play</button>
      <button class="btn" id="playDaily">Daily ribbon · ${escapeHtml(o.dateStr)}</button>
      <button class="btn" id="playFriends">Play with friends</button>
    </div>
    <p class="best-line">${o.best > 0 ? `Best in ${escapeHtml(mode.name)}: <strong>${fmtScore(o.best)}</strong>` : '&nbsp;'}</p>

    <div class="menu-links">
      <button class="linkbtn" id="help">How to play</button>
      <button class="linkbtn" id="about">About</button>
      <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
    </div>
  </section>`;
}

export function helpHtml(): string {
  return `
  <button class="sheet-close" aria-label="Close">✕</button>
  <h2>How to play</h2>
  <ol class="help-list">
    <li>Your <strong>voice is the controller</strong> — hum low to fly low, whistle or sing high to fly high.</li>
    <li>Keep the glowing <strong>mote on the ribbon</strong> to score. Stay clean and your <strong>combo</strong> climbs.</li>
    <li><strong>No mic?</strong> Slide a finger up and down anywhere on the board to fly instead — it's the same game.</li>
    <li>In <strong>Thread</strong>, the ribbon is narrow and the walls have gaps — miss one and the run ends.</li>
  </ol>
  <p class="help-controls"><strong>Desktop:</strong> <kbd>↑</kbd>/<kbd>↓</kbd> (or <kbd>W</kbd>/<kbd>S</kbd>) fly · <kbd>P</kbd> pause · <kbd>R</kbd> restart. The mic works too if you allow it.</p>`;
}

export function aboutHtml(): string {
  return `
  <button class="sheet-close" aria-label="Close">✕</button>
  <h2>About Warble</h2>
  <p>A voice-controlled flying game. On a phone the microphone measures the <strong>pitch</strong> of your
     voice — nothing is recorded, stored, or sent anywhere; the game only ever uses a single number for how
     high you're singing. With no mic (or if you decline it) you slide to fly, which is a real game in its
     own right.</p>
  <p>Play solo, chase the daily ribbon, or share a room link and race friends on the identical course —
     peer-to-peer, with no game server. Connecting to friends uses a public signaling relay only to broker
     the direct link; no gameplay is stored anywhere.</p>
  <p>${ATTRIB}</p>`;
}

function breakdownLine(mode: Mode, sum: Summary): string {
  const acc = `${Math.round(sum.accuracy * 100)}% on the ribbon`;
  const combo = `×${sum.bestCombo} best combo`;
  if (mode.kind === 'thread') {
    const gates = `${sum.gatesPassed}/${sum.gatesTotal} gates`;
    return sum.died
      ? `${gates} · fell at gate ${sum.gatesPassed + 1} · ${acc}`
      : `${gates} · flew the whole ribbon · ${acc} · ${combo}`;
  }
  if (mode.kind === 'steps') {
    return `${sum.notesHit}/${sum.notesTotal} notes held · ${acc} · ${combo}`;
  }
  return `${acc} · ${combo}`;
}

export function soloResultsHtml(o: { mode: Mode; summary: Summary; best: number; isNewBest: boolean; daily: boolean }): string {
  const s = o.summary;
  return `
  <section class="screen results">
    <h2 class="res-title">${o.isNewBest ? 'New best!' : s.died ? 'Run over' : 'Nice flight'}</h2>
    <p class="res-score">${fmtScore(s.score)}<span class="res-score-cap">score</span></p>
    <p class="res-sub">${escapeHtml(o.mode.name)}${o.daily ? ' · daily' : ''} · best ${fmtScore(o.best)}</p>
    <ul class="orb-list">
      <li class="orb-row"><span class="orb-out">${breakdownLine(o.mode, s)}</span></li>
    </ul>
    <p class="res-note">A perfect run stays dead-on the ribbon the whole way — chase 100%.</p>
  </section>`;
}

export function multiResultsHtml(ranked: PeerResult[], selfSeat: number | null, mode: Mode, tally: number[]): string {
  const top = ranked.find((r) => !r.left);
  const rows = ranked
    .map((r, i) => {
      const me = r.seat === selfSeat;
      const rank = r.left ? '—' : r.finished ? `${i + 1}` : '…';
      const wins = tally[r.seat] ?? 0;
      const isWinner = top && r.seat === top.seat && r.finished && !r.left;
      const detail = r.left
        ? 'left'
        : r.finished
          ? `${Math.round(r.accuracy)}% · ×${r.bestCombo}`
          : `${r.progress}% flown`;
      return `<li class="mp-row${me ? ' me' : ''}${r.left ? ' left' : ''}">
        <span class="mp-rank">${rank}</span>
        <span class="mp-name">${escapeHtml(r.name)}${me ? ' <em>(you)</em>' : ''}${isWinner ? ' <span class="mp-star" title="Highest score">★</span>' : ''}<br><small class="mp-detail">${detail}</small></span>
        <span class="mp-score">${r.left ? '—' : fmtScore(r.score)}</span>
        <span class="mp-wins">${wins > 0 ? `${wins}★` : ''}</span>
      </li>`;
    })
    .join('');
  return `
    <h2 class="res-title">Results</h2>
    <p class="res-sub">${escapeHtml(mode.name)} · highest score wins</p>
    <ul class="mp-list">${rows}</ul>`;
}
